/* Zenfusion Flow — shared components. No dependencies. */

/* <zf-slider data-autoplay="5000" data-loop>
     <ul class="zf-slider__track">...<li>slide</li>...</ul>
     <div class="zf-slider__nav"> [data-prev] [data-dots] [data-next] </div>
   </zf-slider> */
if (!customElements.get('zf-slider')) {
  class ZfSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.zf-slider__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.loop = this.hasAttribute('data-loop');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.buildDots();
      this.onScroll = this.update.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => { this.buildDots(); this.update(); });
      this.resizeObserver.observe(this.track);
      const delay = parseInt(this.dataset.autoplay, 10);
      if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => { if (!this.paused) this.go(1, true); }, delay);
        ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => (this.paused = true), { passive: true }));
        ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      }
      this.update();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver && this.resizeObserver.disconnect();
    }
    get step() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }
    get perView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.step));
    }
    get pages() {
      return Math.max(1, this.slides.length - this.perView + 1);
    }
    get index() {
      return Math.round(this.track.scrollLeft / this.step);
    }
    go(dir, auto) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (dir > 0 && this.track.scrollLeft >= max) {
        if (this.loop || auto) this.track.scrollTo({ left: 0 });
        return;
      }
      if (dir < 0 && this.track.scrollLeft <= 2) {
        if (this.loop) this.track.scrollTo({ left: this.track.scrollWidth });
        return;
      }
      this.track.scrollBy({ left: dir * this.step });
    }
    goTo(i) {
      this.track.scrollTo({ left: i * this.step });
    }
    buildDots() {
      if (!this.dotsWrap) return;
      const n = this.pages;
      if (this.dotsWrap.childElementCount === n) return;
      this.dotsWrap.innerHTML = '';
      if (n < 2) return;
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'zf-slider__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(b);
      }
    }
    update() {
      const i = this.index;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (!this.loop) {
        this.prev && (this.prev.disabled = this.track.scrollLeft <= 2);
        this.next && (this.next.disabled = this.track.scrollLeft >= max);
      }
      if (this.dotsWrap) Array.from(this.dotsWrap.children).forEach((d, n) => d.setAttribute('aria-current', n === i));
      const hideNav = this.track.scrollWidth <= this.track.clientWidth + 2;
      const nav = this.querySelector('.zf-slider__nav');
      nav && (nav.hidden = hideNav);
    }
  }
  customElements.define('zf-slider', ZfSlider);
}

/* <zf-video> — click-to-load YouTube/Vimeo/video. Template child holds the real embed. */
if (!customElements.get('zf-video')) {
  class ZfVideo extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-zf-play]');
      btn && btn.addEventListener('click', () => this.load(), { once: true });
    }
    load() {
      const tpl = this.querySelector('template');
      if (!tpl) return;
      const node = tpl.content.firstElementChild.cloneNode(true);
      this.querySelectorAll(':scope > :not(template)').forEach((el) => el.remove());
      this.appendChild(node);
      if (node.tagName === 'VIDEO') node.play && node.play();
    }
  }
  customElements.define('zf-video', ZfVideo);
}

/* <zf-countdown data-end="2026-12-31T23:59:59" data-evergreen-minutes="15" data-key="x">
   children: [data-d] [data-h] [data-m] [data-s]; optional [data-expired] */
if (!customElements.get('zf-countdown')) {
  class ZfCountdown extends HTMLElement {
    connectedCallback() {
      this.end = this.resolveEnd();
      if (!this.end) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    resolveEnd() {
      const mins = parseInt(this.dataset.evergreenMinutes, 10);
      if (mins > 0) {
        const key = 'zf-cd-' + (this.dataset.key || 'default');
        let end;
        try { end = parseInt(sessionStorage.getItem(key), 10); } catch (e) {}
        if (!end || end < Date.now()) {
          end = Date.now() + mins * 60000;
          try { sessionStorage.setItem(key, end); } catch (e) {}
        }
        return end;
      }
      const t = Date.parse(this.dataset.end || '');
      return isNaN(t) ? null : t;
    }
    tick() {
      let diff = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      const d = Math.floor(diff / 864e5); diff -= d * 864e5;
      const h = Math.floor(diff / 36e5); diff -= h * 36e5;
      const m = Math.floor(diff / 6e4); diff -= m * 6e4;
      const s = Math.floor(diff / 1e3);
      const set = (sel, v) => this.querySelectorAll(sel).forEach((el) => (el.textContent = v));
      set('[data-d]', pad(d)); set('[data-h]', pad(h)); set('[data-m]', pad(m)); set('[data-s]', pad(s));
      if (this.end - Date.now() <= 0) {
        clearInterval(this.timer);
        this.classList.add('is-expired');
        if (this.hasAttribute('data-hide-on-expire')) this.closest('[data-zf-countdown-wrap]')?.setAttribute('hidden', '');
      }
    }
  }
  customElements.define('zf-countdown', ZfCountdown);
}

/* <zf-counter data-to="100000" data-suffix="+"> animates a number when visible */
if (!customElements.get('zf-counter')) {
  class ZfCounter extends HTMLElement {
    connectedCallback() {
      const to = parseFloat(this.dataset.to);
      if (isNaN(to)) return;
      const fmt = (n) => (this.dataset.prefix || '') + Math.round(n).toLocaleString() + (this.dataset.suffix || '');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { this.textContent = fmt(to); return; }
      this.textContent = fmt(0);
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const dur = parseInt(this.dataset.duration, 10) || 1600;
        const t0 = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - t0) / dur);
          this.textContent = fmt(to * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
      io.observe(this);
    }
  }
  customElements.define('zf-counter', ZfCounter);
}

/* [data-zf-modal-open="dialog-id"] opens a <dialog class="zf-modal" id="dialog-id"> */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-zf-modal-open]');
  if (opener) {
    const dlg = document.getElementById(opener.dataset.zfModalOpen);
    if (dlg && dlg.showModal) { e.preventDefault(); dlg.showModal(); }
    return;
  }
  const closer = e.target.closest('[data-zf-modal-close]');
  if (closer) { closer.closest('dialog')?.close(); return; }
  if (e.target.tagName === 'DIALOG' && e.target.classList.contains('zf-modal')) e.target.close();
});

/* Shared add-to-cart helper used by zf sections (bundle, add-ons, quick add).
   items: [{id, quantity, properties}] — refreshes Dawn cart drawer / bubble when present. */
window.zfAddToCart = async function (items, opener) {
  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const body = { items };
  if (cart && cart.getSectionsToRender) body.sections = cart.getSectionsToRender().map((s) => s.id);
  body.sections_url = window.location.pathname;
  const res = await fetch(window.routes.cart_add_url + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
  if (cart && cart.renderContents) {
    if (!data.sections) data.sections = {};
    cart.setActiveElement && opener && cart.setActiveElement(opener);
    data.key = data.items && data.items[0] && data.items[0].key;
    try { cart.renderContents(data); } catch (err) { window.location.href = window.routes.cart_url; }
    cart.classList && cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
  } else {
    window.location.href = window.routes.cart_url;
  }
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zf', cartData: data });
  }
  return data;
};
